<template>
    <BaseView>
        <div class="bg-surface-0 dark:bg-surface-900 shadow rounded-border p-4 h-full relative m-auto overflow-scroll">
            <Tabs value="0" lazy>
                <TabList>
                    <Tab value="0">System Settings</Tab>
                    <Tab value="1">Logs</Tab>
                    <Tab value="2">SLA Report</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel value="0">

                        <div class="flex flex-col items-start relative">
                            <!-- <LoadingState v-if="isLoading" overlay></LoadingState> -->
                            <span class="text-xl">Celonis EMS Url</span>
                            <div>
                                {{ emsUrl }}
                            </div>

                            <span class="text-xl mt-2">Celonis Authentication</span>
                            <div v-if="apiStatusLoading">
                                <LoadingState small></LoadingState>
                            </div>
                            <div v-else>
                                <div v-if="apiStatus !== null">
                                    <Message :severity="apiStatus ? 'success' : 'error'">Authentication is
                                        {{ apiStatus ? '' : 'in' }}valid
                                    </Message>
                                </div>
                                <div class="mt-1 flex flex-col gap-1">
                                    Update Api key:
                                    <InputText type="password" v-model="newApiKey"></InputText>
                                </div>
                                <div class="mt-1 flex flex-col gap-1">
                                    Update Oauth Client ID:
                                    <InputText type="password" v-model="newClientID"></InputText>
                                </div>
                                <div class="mt-1 flex flex-col gap-1">
                                    Update Oauth Client Secret:
                                    <InputText type="password" v-model="newClientSecret"></InputText>
                                </div>
                                <div class="mt-1 flex items-center gap-1">
                                    <Button label="Update"
                                            rounded
                                            icon="pi pi-arrow-up"
                                            :disabled="!newApiKey && !newClientID && !newClientSecret || apiUpdateLoading" @click="update"></Button>
                                    <LoadingState v-if="apiUpdateLoading" small></LoadingState>
                                    <Message class="ml-1" v-if="updateStatus !== null && !apiUpdateLoading"
                                             :severity="updateStatus ? 'success' : 'error'">
                                        {{ updateStatus ? 'Authentication updated' : 'Failed, provide a valid authentication value' }}
                                    </Message>
                                </div>
                            </div>
                        </div>
                        <div class="mt-2 flex items-center gap-1">
                            <Button rounded icon="pi pi-sync" label="Check permissions"
                                    @click="loadPermissions" :disabled="permLoading"></Button>
                            <LoadingState v-if="permLoading" small></LoadingState>

                        </div>
                        <div class="mt-2 h-full" v-if="datapools && emsSpaces">
                            <Card>
                                <template #title>
                                    <span>Datapools ({{ datapools.length }})</span>
                                </template>
                                <template #content>
                                    <DataTable :value="datapools" scrollable>
                                        <Column field="name" header="Name"></Column>
                                        <Column v-for="perm in datapoolPermList" :field="perm.key" :header="perm.label">
                                            <template #body="{data}">
                                                <span>{{ data.permissions.includes(perm.key) ? 'Yes' : 'No' }}</span>
                                            </template>
                                        </Column>
                                    </DataTable>
                                </template>
                            </Card>
                            <Card class="mt-2">
                                <template #title>
                                    <span>Ems Spaces ({{ emsSpaces.length }})</span>
                                </template>
                                <template #content>
                                    <DataTable :value="emsSpaces">
                                        <Column field="name" header="Name"></Column>
                                        <Column v-for="perm in emsSpacePermList" :field="perm.key" :header="perm.label">
                                            <template #body="{data}">
                                                <span>{{ data.permissions.includes(perm.key) ? 'Yes' : 'No' }}</span>
                                            </template>
                                        </Column>
                                    </DataTable>
                                </template>
                            </Card>
                        </div>
                    </TabPanel>
                    <TabPanel value="1">
                        <AuditLogs/>
                    </TabPanel>
                    <TabPanel value="2">
                        <SLAReport/>
                    </TabPanel>
                </TabPanels>
            </Tabs>

        </div>
    </BaseView>
</template>
<script setup>
import AuditLogs from "@/src/views/system/AuditLogs.vue";
import {onMounted, ref, computed} from 'vue';
import {useStore} from 'vuex';
import {CELONIS_CONTENT_API_TYPE, DATA_API_TYPE} from "@/src/model/apiTypes";
import BaseView from "@/src/views/BaseView.vue";
import _ from 'lodash-es';
import SLAReport from "@/src/views/system/SLAReport.vue";

const store = useStore();

const emsUrl = ref('');
const apiStatus = ref()
const newApiKey = ref('')
const newClientID = ref('')
const newClientSecret = ref('')

const datapools = ref(null)
const emsSpaces = ref(null)

const datapoolPermList = [
    {key: "USE_ALL_DATA_MODELS", label: "USE ALL DATA MODELS"},
    {key: "VIEW_DATA_POOL", label: "VIEW DATA POOL"},
    {key: "EDIT_DATA_POOL_RESTRICTED", label: "EDIT DATA POOL"},
    {key: "ADMIN", label: "MANAGE DATA POOL"},
    {key: "DATA_PUSH_API", label: "DATA PUSH API"},
    {key: "CONTINUOUS_DATA_PUSH_API", label: "CONTINUOUS DATA PUSH API"},
]

const emsSpacePermList = [
    {key: "USE_ALL_PACKAGES", label: "USE ALL PACKAGES"},
    {key: "EDIT_SPACE", label: "EDIT SPACE"},
    {key: "EDIT_ALL_PACKAGES", label: "EDIT ALL PACKAGES"},
    {key: "DELETE_ALL_PACKAGES", label: "DELETE ALL PACKAGES"},
    {key: "DELETE_SPACE", label: "DELETE SPACE"},
    {key: "CREATE_PACKAGE", label: "CREATE PACKAGE"},
    {key: "MANAGE_PERMISSIONS", label: "MANAGE PERMISSIONS"},
]

onMounted(async () => {
    emsUrl.value = await store.dispatch('getEmsUrl');
    apiStatus.value = await store.dispatch('getCelonisApiStatus');
});

const apiStatusLoading = computed(() => store.getters.isLoading([
    CELONIS_CONTENT_API_TYPE.GET_CELONIS_API,
]));

const apiUpdateLoading = computed(() => store.getters.isLoading([
    CELONIS_CONTENT_API_TYPE.UPDATE_CELONIS_API,
]));

const updateStatus = ref(null)
const update = async () => {
    updateStatus.value = await store.dispatch('updateCelonisApi', {
      'apiKey': newApiKey.value,
      'clientID': newClientID.value,
      'clientSecret': newClientSecret.value})
}

const permLoading = computed(() => store.getters.isLoading([
    CELONIS_CONTENT_API_TYPE.GET_STUDIO_SPACES,
    DATA_API_TYPE.GET_DATAPOOLS,
]));

const loadPermissions = async () => {
    datapools.value = await store.dispatch('repoStore/getDatapools', true)
    emsSpaces.value = await store.dispatch('graphStore/loadEmsSpaces', true)
}
</script>

<style>
.p-tablist {
    overflow: unset !important;
}
</style>